import {
    AdditionalProductFragment,
    VariantFragment,
} from '@graphql/generated/graphql';
import { useState, useCallback } from 'react';

export type Select = {
    name: string;
    options: { value: string; disabled?: boolean }[];
    value?: string;
};

interface UseSelectsReturn {
    additionalProduct: AdditionalProductFragment | null;
    selectedVariant: VariantFragment | null;
    selects: Select[];
    onSelectOption: (optionName: string, value: string) => void;
}

interface Props {
    additionalProduct: AdditionalProductFragment | null;
    defaultSelectedVariant?: VariantFragment | null;
}

export const useVariantSelects = ({
    additionalProduct,
    defaultSelectedVariant,
}: Props): UseSelectsReturn => {
    const getOptionMap = useCallback((variants: VariantFragment[]) => {
        const optionMap: Record<string, Set<string>> = {};
        // Build list of options and unique values
        for (const variant of variants) {
            for (const opt of variant.Options ?? []) {
                if (!optionMap[opt.name]) optionMap[opt.name] = new Set();
                optionMap[opt.name].add(opt.value);
            }
        }
        return optionMap;
    }, []);

    const buildSelects = useCallback(
        (
            variants: VariantFragment[],
            selectedVariant?: VariantFragment | null
        ) => {
            // Collect all possible options for each name
            const optionMap = getOptionMap(variants);

            // Build list of selects
            return Object.keys(optionMap).map((name) => ({
                name,
                options: Array.from(optionMap[name]).map((o) => ({
                    value: o,
                    disabled: false,
                })),
                value: selectedVariant?.Options?.find(
                    (opt) => opt.name === name
                )?.value,
            }));
        },
        [getOptionMap]
    );

    const getInitialSelectedVariant = useCallback(() => {
        if (defaultSelectedVariant) {
            return defaultSelectedVariant;
        }

        if (additionalProduct?.Variants?.length === 1) {
            return additionalProduct.Variants[0];
        }

        return null;
    }, [additionalProduct, defaultSelectedVariant]);

    const [selects, setSelects] = useState<Select[]>(
        buildSelects(
            additionalProduct?.Variants ?? [],
            getInitialSelectedVariant()
        )
    );

    const [selectedVariant, setSelectedVariant] =
        useState<VariantFragment | null>(getInitialSelectedVariant());

    const filterVariants = useCallback(
        (variants: VariantFragment[], selects: Select[]) => {
            return variants.filter((variant) => {
                return selects.every((select) => {
                    return variant.Options?.some(
                        (option) =>
                            option.name === select.name &&
                            option.value === select.value
                    );
                });
            });
        },
        []
    );

    const updateSelectsDisabled = useCallback(
        (variants: VariantFragment[], selects: Select[]) => {
            const remainingOptions = getOptionMap(variants);
            const restrictedSelects = selects.map((select) => {
                return {
                    name: select.name,
                    options: select.options.map((option) => {
                        if (remainingOptions[select.name]?.has(option.value)) {
                            return option;
                        }
                        return {
                            ...option,
                            disabled: true,
                        };
                    }),
                    value: select.value,
                };
            });
            return restrictedSelects;
        },
        [getOptionMap]
    );

    const onSelectOption = useCallback(
        (optionName: string, value: string) => {
            const variants = additionalProduct?.Variants ?? [];
            const updatedSelects = selects.map((select) => {
                if (select.name === optionName) {
                    return {
                        ...select,
                        value,
                    };
                }
                return select;
            });
            const filteredVariants = filterVariants(variants, updatedSelects);
            // Here we could restrict the options of the selects based on the remaining variants but the UI is not scoped yet
            // const restrictedSelects = updateSelectsDisabled(
            //     filteredVariants,
            //     updatedSelects
            // );

            setSelectedVariant(
                filteredVariants.length === 1 ? filteredVariants[0] : null
            );
            setSelects(updatedSelects);
        },
        [additionalProduct, filterVariants, selects]
    );

    return {
        additionalProduct,
        selectedVariant,
        selects,
        onSelectOption,
    };
};
